import { useEffect, useState } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import { ChevronLeft, Calendar, Tag, Clock, Share2, Facebook, Twitter, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { getNewsArticle, getNews } from "@/lib/api";
import { formatDateLong } from "@/lib/dateUtils";

const formatDate = (dateStr) => formatDateLong(dateStr);

const categoryLabels = {
  nieuws: { label: "Nieuws", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  wedstrijd: { label: "Wedstrijd", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  club: { label: "Club", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  jeugd: { label: "Jeugd", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  evenement: { label: "Evenement", color: "bg-pink-500/20 text-pink-400 border-pink-500/30" }
};

// Calculate reading time based on word count (avg 200 words/minute for Dutch)
const calculateReadingTime = (content) => {
  if (!content) return 1;
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return Math.max(1, minutes); // Minimum 1 minute
};

// Calculate relevance score between two articles
const getRelevanceScore = (article1, article2) => {
  let score = 0;
  
  // Same category = +3 points
  if (article1.category === article2.category) {
    score += 3;
  }
  
  // Check for common words in titles (excluding common Dutch words)
  const stopWords = ['de', 'het', 'een', 'van', 'in', 'op', 'voor', 'met', 'aan', 'en', 'is', 'te', 'bij', 'naar', 'om'];
  const words1 = article1.title.toLowerCase().split(/\s+/).filter(w => w.length > 3 && !stopWords.includes(w));
  const words2 = article2.title.toLowerCase().split(/\s+/).filter(w => w.length > 3 && !stopWords.includes(w));
  
  // +1 point for each common word in title
  words1.forEach(word => {
    if (words2.includes(word)) score += 1;
  });
  
  // Published within 30 days of each other = +1 point
  if (article1.published_at && article2.published_at) {
    const date1 = new Date(article1.published_at);
    const date2 = new Date(article2.published_at);
    const daysDiff = Math.abs(date1 - date2) / (1000 * 60 * 60 * 24);
    if (daysDiff <= 30) score += 1;
  }
  
  return score;
};

export default function NewsDetail() {
  const { id } = useParams();
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getNewsArticle(id);
        setArticle(response.data);
        
        // Fetch related articles based on relevance
        const newsResponse = await getNews();
        const otherArticles = newsResponse.data.filter(a => a.id !== id);
        
        // Calculate relevance scores and sort
        const articlesWithScores = otherArticles.map(a => ({
          ...a,
          relevanceScore: getRelevanceScore(response.data, a)
        }));
        
        // Sort by relevance score (highest first), then by date (newest first)
        articlesWithScores.sort((a, b) => {
          if (b.relevanceScore !== a.relevanceScore) {
            return b.relevanceScore - a.relevanceScore;
          }
          return new Date(b.published_at) - new Date(a.published_at);
        });
        
        // Take top 3 related articles
        setRelatedArticles(articlesWithScores.slice(0, 3));
      } catch (error) {
        console.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const shareUrl = window.location.href;
  const shareTitle = article?.title || '';

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className={`h-8 w-32 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded mb-8`} />
            <div className={`h-80 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded-2xl mb-8`} />
            <div className="space-y-4">
              <div className={`h-10 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded w-3/4`} />
              <div className={`h-4 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded w-1/4`} />
              <div className={`h-4 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded`} />
              <div className={`h-4 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded`} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'} mb-4`}>Artikel niet gevonden</h1>
          <Button asChild variant="outline" className={isDark ? "border-white/20 text-white hover:bg-white/10" : "border-slate-300"}>
            <Link to="/nieuws">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Terug naar Nieuws
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const catInfo = categoryLabels[article.category] || categoryLabels.nieuws;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`} data-testid="news-detail-page">
      {/* Hero Section with Image */}
      {article.image_url && (
        <div className="relative h-[50vh] min-h-[400px] max-h-[600px]">
          <div className="absolute inset-0">
            <img 
              src={article.image_url} 
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
          </div>
          
          {/* Back Button on Hero */}
          <div className="absolute top-6 left-0 right-0">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <Button 
                asChild 
                variant="ghost" 
                className="text-white/80 hover:text-white hover:bg-white/10 backdrop-blur-sm"
                data-testid="back-to-news"
              >
                <Link to="/nieuws">
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Terug naar Nieuws
                </Link>
              </Button>
            </div>
          </div>

          {/* Title on Hero */}
          <div className="absolute bottom-0 left-0 right-0 pb-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Badge className={catInfo.color}>
                  <Tag className="h-3 w-3 mr-1" />
                  {catInfo.label}
                </Badge>
                {article.is_featured && (
                  <Badge className="bg-[hsl(48,96%,53%)]/20 text-[hsl(48,96%,53%)] border-[hsl(48,96%,53%)]/30">
                    Uitgelicht
                  </Badge>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white font-['Chivo'] leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button (when no image) */}
        {!article.image_url && (
          <Button 
            asChild 
            variant="ghost" 
            className={`${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} mb-8`}
            data-testid="back-to-news"
          >
            <Link to="/nieuws">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Terug naar Nieuws
            </Link>
          </Button>
        )}

        {/* Title (when no image) */}
        {!article.image_url && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Badge className={catInfo.color}>
                <Tag className="h-3 w-3 mr-1" />
                {catInfo.label}
              </Badge>
              {article.is_featured && (
                <Badge className="bg-[hsl(48,96%,53%)]/20 text-[hsl(48,96%,53%)] border-[hsl(48,96%,53%)]/30">
                  Uitgelicht
                </Badge>
              )}
            </div>
            <h1 className={`text-3xl md:text-4xl lg:text-5xl font-black ${isDark ? 'text-white' : 'text-slate-900'} font-['Chivo'] mb-6 leading-tight`}>
              {article.title}
            </h1>
          </>
        )}

        {/* Meta Info Bar */}
        <div className={`flex flex-wrap items-center justify-between gap-4 py-4 mb-8 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
          <div className="flex items-center gap-4">
            <span className={`text-sm flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <Calendar className="h-4 w-4" />
              {formatDate(article.published_at)}
            </span>
            <span className={`text-sm flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <Clock className="h-4 w-4" />
              {calculateReadingTime(article.content)} min leestijd
            </span>
          </div>
          
          {/* Share Buttons */}
          <div className="flex items-center gap-2">
            <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'} mr-2`}>Delen:</span>
            <Button 
              size="sm" 
              variant="ghost" 
              className={isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}
              onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
            >
              <Facebook className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              className={isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}
              onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`, '_blank')}
            >
              <Twitter className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              className={isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}
              onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`, '_blank')}
            >
              <Linkedin className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
            </Button>
          </div>
        </div>

        {/* Summary */}
        {article.summary && (
          <p className={`text-xl ${isDark ? 'text-slate-300' : 'text-slate-600'} mb-8 leading-relaxed font-medium`}>
            {article.summary}
          </p>
        )}

        {/* Content */}
        <article className={`prose ${isDark ? 'prose-invert' : ''} prose-lg max-w-none`}>
          <div 
            className={`${isDark ? 'text-slate-200' : 'text-slate-700'} leading-relaxed text-base md:text-lg [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:mt-6 [&>h2]:mb-4 [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:mt-4 [&>h3]:mb-2 [&>p]:mb-4 [&>ul]:list-disc [&>ul]:ml-6 [&>ol]:list-decimal [&>ol]:ml-6 [&>strong]:font-bold [&>em]:italic`}
            dangerouslySetInnerHTML={{ __html: article.content || '' }}
          />
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className={`mt-16 pt-8 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'} mb-6`}>
              Gerelateerde artikelen
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link key={related.id} to={`/nieuws/${related.id}`}>
                  <Card className={`overflow-hidden group cursor-pointer transition-all duration-300 ${
                    isDark 
                      ? 'bg-slate-800/50 border-white/10 hover:border-white/20 hover:bg-slate-800' 
                      : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg'
                  }`}>
                    {related.image_url && (
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={related.image_url} 
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <Badge className={`${categoryLabels[related.category]?.color || catInfo.color} text-xs mb-2`}>
                        {categoryLabels[related.category]?.label || related.category}
                      </Badge>
                      <h3 className={`font-semibold line-clamp-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {related.title}
                      </h3>
                      <p className={`text-sm mt-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {formatDate(related.published_at)}
                      </p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
